import React, { useState } from 'react';
import { toast } from 'sonner';
import { getCheckoutUrl, getPoofAPIUrl } from '@/lib/config';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy } from 'lucide-react';

const ALL_RAILS = ['usdc', 'sol', 'usdt', 'whop'];

interface CreateSessionModalProps {
  open: boolean;
  onClose: () => void;
  apiKey: string;
  appid: string;
}

export const CreateSessionModal: React.FC<CreateSessionModalProps> = ({ open, onClose, apiKey, appid }) => {
  const [tenantUserId, setTenantUserId] = useState('');
  const [amountUsd, setAmountUsd] = useState('');
  const [successRedirectUrl, setSuccessRedirectUrl] = useState('');
  const [selectedRails, setSelectedRails] = useState<string[]>(['usdc']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ sessionId: string; payUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const toggleRail = (rail: string) => {
    setSelectedRails((prev) =>
      prev.includes(rail) ? prev.filter((r) => r !== rail) : [...prev, rail]
    );
  };

  const handleCreate = async () => {
    if (!tenantUserId || !amountUsd) return toast.error('Fill all fields');
    const cents = Math.round(parseFloat(amountUsd) * 100);
    if (isNaN(cents) || cents <= 0) return toast.error('Invalid amount');
    if (selectedRails.length === 0) return toast.error('Select at least one rail');
    const redirect = successRedirectUrl.trim();
    if (redirect) {
      try {
        const url = new URL(redirect);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          return toast.error('Redirect URL must use http or https');
        }
      } catch {
        return toast.error('Redirect URL must be an absolute URL');
      }
    }

    setLoading(true);
    try {
      const url = getPoofAPIUrl('/api/v1/sessions');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantUserId,
          amountUsdCents: cents,
          accepts: selectedRails,
          ...(redirect ? { successRedirectUrl: redirect } : {}),
        }),
      });
      let json: any = null;
      try { json = await res.json(); } catch { /* not JSON */ }
      if (!res.ok || !json?.success) {
        toast.error(json?.error?.message || `Request failed (${res.status})`);
        return;
      }
      const sessionId = json.data.sessionId;
      setResult({
        sessionId,
        payUrl: typeof json.data.payUrl === 'string' ? json.data.payUrl : getCheckoutUrl(`/pay/${sessionId}`),
      });
      toast.success('Session created');
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!result?.payUrl) return;
    await navigator.clipboard.writeText(result.payUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
    toast.success('Pay URL copied');
  };

  const handleClose = () => {
    setResult(null);
    setTenantUserId('');
    setAmountUsd('');
    setSuccessRedirectUrl('');
    setSelectedRails(['usdc']);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-md' style={{ background: 'var(--pp-surface)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--pp-text)' }}>Create Session</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className='flex flex-col gap-4'>
            <div className='text-sm font-medium' style={{ color: 'var(--pp-settled)' }}>
              Session created successfully
            </div>
            <div
              className='rounded-lg border p-3 flex items-center justify-between gap-2'
              style={{ background: 'var(--pp-subtle)', borderColor: 'var(--pp-border)' }}
            >
              <span style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.75rem', color: 'var(--pp-text)', wordBreak: 'break-all' }}>
                {result.payUrl}
              </span>
              <button onClick={handleCopyUrl} style={{ color: 'var(--pp-text-muted)', flexShrink: 0 }}>
                {copied ? '✓' : <Copy size={14} />}
              </button>
            </div>
            <div className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
              Session ID: <span style={{ fontFamily: 'var(--pp-mono)' }}>{result.sessionId}</span>
            </div>
            <div className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
              Product: <span style={{ fontFamily: 'var(--pp-mono)' }}>{appid}</span>
            </div>
            <Button onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            <div className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
              Product: <span style={{ fontFamily: 'var(--pp-mono)' }}>{appid}</span>
            </div>

            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='userId' style={{ color: 'var(--pp-text)' }}>Tenant User ID</Label>
              <Input
                id='userId'
                placeholder='user_123'
                value={tenantUserId}
                onChange={(e) => setTenantUserId(e.target.value)}
              />
            </div>

            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='amount' style={{ color: 'var(--pp-text)' }}>Amount (USD)</Label>
              <Input
                id='amount'
                placeholder='29.00'
                type='number'
                step='0.01'
                value={amountUsd}
                onChange={(e) => setAmountUsd(e.target.value)}
              />
            </div>

            <div className='flex flex-col gap-1.5'>
              <Label style={{ color: 'var(--pp-text)' }}>Payment Rails</Label>
              <div className='flex gap-2 flex-wrap'>
                {ALL_RAILS.map((rail) => (
                  <button
                    key={rail}
                    onClick={() => toggleRail(rail)}
                    className='px-3 py-1.5 rounded border text-xs font-medium uppercase transition-colors'
                    style={{
                      borderColor: selectedRails.includes(rail) ? 'var(--pp-action)' : 'var(--pp-border)',
                      background: selectedRails.includes(rail) ? 'var(--pp-soft-blue)' : 'transparent',
                      color: selectedRails.includes(rail) ? 'var(--pp-action)' : 'var(--pp-text-muted)',
                    }}
                  >
                    {rail}
                  </button>
                ))}
              </div>
            </div>

            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='successRedirectUrl' style={{ color: 'var(--pp-text)' }}>Success Redirect URL</Label>
              <Input
                id='successRedirectUrl'
                placeholder='https://your-app.com/checkout/complete'
                value={successRedirectUrl}
                onChange={(e) => setSuccessRedirectUrl(e.target.value)}
              />
            </div>

            <Button onClick={handleCreate} disabled={loading} style={{ background: 'var(--pp-action)' }}>
              {loading ? 'Creating…' : 'Create Session'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateSessionModal;
